import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import type { ApexAxisChartSeries } from 'ng-apexcharts';
import {
  GapSlider,
  GapSliderInterface,
} from '../../../interfaces/gap-slider.interface';
import {
  DepreciationItem,
  depreciationTable,
} from '../../pages/apk/models/depreciation-table.class';
import {
  gapPayout,
  getVehiclePriceHomePage,
} from '../../shared/vehicle-depreciation.util';
import { positiveIntOnly } from '../../pages/apk/apk/validators';

@Injectable({
  providedIn: 'root',
})
export class DepreciationChartService {
  private depreciationTable: DepreciationItem[] = depreciationTable;
  private _gapSliderFormGroup: UntypedFormGroup;
  private _gapSliderDto: GapSliderInterface;

  get gapSliderFormGroup(): UntypedFormGroup {
    return this._gapSliderFormGroup;
  }

  set gapSliderFormGroup(value: UntypedFormGroup) {
    this._gapSliderFormGroup = value;
  }

  get gapSliderDto(): GapSliderInterface {
    return this._gapSliderDto;
  }

  set gapSliderDto(value: GapSliderInterface) {
    this._gapSliderDto = value;
  }

  constructor(
    private fb: UntypedFormBuilder,
  ) {}

  public getSeries(
    vehicleInsuredSum: number,
    insurancePeriodMax: number,
  ): {
    chartSeries: ApexAxisChartSeries;
    chartSeriesCategories: string[];
    chartBarSeries: ApexAxisChartSeries;
  } {
    const response = {
      chartSeries: [
        {
          name: 'Wartość pojazdu',
          data: [vehicleInsuredSum],
        },
      ],
      chartSeriesCategories: ['Dziś'],
      chartBarSeries: [
        {
          name: 'offenderInsurance',
          data: [],
        },
        {
          name: 'gapInsurance',
          data: [],
        },
        {
          name: 'insuranceLoss',
          data: [],
        },
      ],
    };

    const insurancePeriodYears = insurancePeriodMax / 12;

    for (let i = 1; i <= insurancePeriodYears; i++) {
      const priceDecreaseForPeriod = this.depreciationTable.find(
        (item) => item.month === i * 12,
      );
      response.chartSeries[0].data[i] = Math.round(
        vehicleInsuredSum -
          vehicleInsuredSum * priceDecreaseForPeriod.priceDecrease,
      );
      response.chartSeriesCategories.push(i + ` ${this.getPluralRules(i)}`);
    }

    return response;
  }

  getPluralRules(insurancePeriod: number) {
    const suffixes = new Map([
      ['1', 'rok'],
      ['2', 'lata'],
      ['3', 'lata'],
      ['4', 'lata'],
      ['5', 'lat'],
      ['6', 'lat'],
    ]);
    const formatOrdinals = (n: number) => {
      const suffix = suffixes.get(n.toString());
      return `${suffix}`;
    };

    return formatOrdinals(insurancePeriod);
  }

  initializeSliderForm(): void {
    this.gapSliderDto = new GapSlider();
    this.gapSliderFormGroup = this.fb.group({
      vehiclePurchasePrice: [
        this.gapSliderDto.vehiclePurchasePrice,
        [positiveIntOnly(), RxwebValidators.maxNumber({ value: 600000 })],
      ],
      monthOfUse: [this.gapSliderDto.monthOfUse],
      monthsArray: [this.gapSliderDto.monthsArray],
      policyValue: [this.gapSliderDto.policyValue, [Validators.required]],
      gapValue: [this.gapSliderDto.gapValue, [Validators.required]],
    });
    this.gapSliderFormGroup.controls.monthOfUse.patchValue(36);
    this.gapSliderFormGroup.controls.monthsArray.patchValue([
      60 - this.gapSliderFormGroup.controls.monthOfUse.value,
      59,
    ]);
  }

  /**
   * Recalculates the payout simulation from the slider month and the price
   * typed in the right-hand field. Does not calculate the insurance premium.
   */
  handleSliding(): void {
    const monthsArray = this.gapSliderFormGroup.controls.monthsArray.value;
    const monthOfUse = Array.isArray(monthsArray)
      ? 60 - Number(monthsArray[0])
      : null;
    this.gapSliderFormGroup.controls.monthOfUse.patchValue(monthOfUse);

    const purchasePrice =
      this.gapSliderFormGroup.controls.vehiclePurchasePrice.value;
    const policyValue =
      monthOfUse == null || !Number.isFinite(monthOfUse)
        ? null
        : getVehiclePriceHomePage(monthOfUse, purchasePrice);

    this.gapSliderFormGroup.controls.policyValue.patchValue(policyValue);
    this.gapSliderFormGroup.controls.gapValue.patchValue(
      gapPayout(purchasePrice, policyValue),
    );
  }
}
